import { forwardRef, ReactNode } from "react";

import { cx } from "@/utils";

import baseStyles from "../Base.module.css";
import styles from "./Faux.module.css";

export interface FauxInputProps
  extends Pick<React.HTMLProps<HTMLInputElement>, "value" | "placeholder">,
    Omit<React.HTMLProps<HTMLButtonElement>, "type"> {
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  errorMessage?: string;
  block?: boolean;
  wrapperClassName?: string;
  error?: boolean;
  constrainValue?: boolean;
}

/**
 * This is an element that closely mimics an Input
 * It can be used for various cases where an input does not work,
 * but we want to mimic the behaviour of an input.
 */
const FauxInput = forwardRef<HTMLSpanElement, FauxInputProps>(
  (
    {
      error = false,
      errorMessage: _,
      className,
      block,
      startAdornment = null,
      endAdornment = null,
      disabled,
      value,
      placeholder,
      wrapperClassName,
      constrainValue,
      children,
      ...rest
    },
    ref
  ) => {
    return (
      <span
        className={cx(
          baseStyles.control,
          { [baseStyles.block]: block },
          wrapperClassName
        )}
        ref={ref}
      >
        {startAdornment}
        <button
          type="button"
          aria-disabled={disabled}
          aria-invalid={error}
          className={cx(
            baseStyles.root,
            styles.root,
            {
              [baseStyles.adornedStart]: startAdornment !== null,
              [baseStyles.adornedEnd]: endAdornment !== null,
            },
            className
          )}
          {...rest}
        >
          {constrainValue ? (
            <span className={styles.constrainValue}>
              {children || value || placeholder}
            </span>
          ) : (
            children || value || placeholder
          )}
        </button>
        {endAdornment}
      </span>
    );
  }
);

FauxInput.displayName = "FauxInput";

export default FauxInput;
